import { faMagnifyingGlass } from '@fortawesome/pro-light-svg-icons';
import { AutocompleteRenderInputParams, InputAdornment } from '@mui/material';
import type { Supersession } from '@partful/shared-types';
import { useTenant } from '@providers/Tenant';
import { makeKitUrl } from '@services/kits/kitUrl';
import { kitsToPartPaths } from '@services/kits/utils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import SearchOption from 'src/components/Product/Desktop/Panels/PartsCatalogue/Autocomplete/SearchOption';
import {
  AutocompleteTextField,
  StyledPopper,
  StyledProductSearch,
  WhiteIcon,
} from 'src/components/Product/Desktop/Panels/PartsCatalogue/Autocomplete/styles';
import filterOptionsFunction, { uniqueKey } from 'src/components/Product/Shared/Helpers/filterSearchOptions';
import { useProduct } from 'src/providers/Product';
import { usePart } from 'src/services/part';
import { PartPathWithHash } from 'src/services/partPath';
import { makePartUrl } from 'src/services/partUrl';

const MINIMUM_SEARCH_TERM_LENGTH = 3;
const INITIAL_SEARCH_TERM = '';
export const CHARACTERS_TO_ESCAPE_REGEX = /[.*+?^${}()|[\]\\]/g;

export interface SearchablePart extends Omit<PartPathWithHash, 'partNumber'>, Omit<Supersession, 'partNumber'> {
  partNumber?: string;
} // Typing is slightly off. Some options do not have name or id, partNumber is considered required but in fact its not (only supersessions have part number, this should be merged with id in EPC).

export function Autocomplete(): JSX.Element {
  const [query, setQuery] = useState(INITIAL_SEARCH_TERM);
  const history = useHistory();
  const { productId, partPaths, kits } = useProduct();
  const { enabledFeatures } = useTenant();
  const { t } = useTranslation('partsCatalogue');

  const enabled = query.length >= MINIMUM_SEARCH_TERM_LENGTH;
  const escapedQuery = query.replace(CHARACTERS_TO_ESCAPE_REGEX, '\\$&');
  const regex = useMemo(() => new RegExp(escapedQuery, 'gi'), [escapedQuery]);

  const part = usePart();

  const searchOptions = useMemo(() => {
    const options = [...flattenSupersessionsAsParts(partPaths)];

    if (enabledFeatures.includes('kits')) {
      options.push(...kitsToPartPaths(kits));
    }

    return options;
  }, [partPaths, kits, enabledFeatures]);

  useEffect(() => {
    // Set to upper case for case-insensitive
    const partNameAllUpperCase = part.current?.name?.toUpperCase();
    const partNumberAllUpperCase = part.current?.partNumber?.toUpperCase();
    const queryAllUpperCase = query.toUpperCase();

    if (partNameAllUpperCase !== queryAllUpperCase && partNumberAllUpperCase !== queryAllUpperCase) {
      setQuery(INITIAL_SEARCH_TERM);
    }
    if (
      query !== '' &&
      (partNameAllUpperCase?.includes(queryAllUpperCase) || partNumberAllUpperCase?.includes(queryAllUpperCase))
    ) {
      setQuery(part.current?.name || /* istanbul ignore next */ '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [part]);

  const renderInput = useCallback(
    (params: AutocompleteRenderInputParams) => {
      const options = filterOptionsFunction(regex, enabled)(flattenSupersessionsAsParts(partPaths));
      return (
        <AutocompleteTextField
          {...params}
          variant="outlined"
          label={t('search')}
          focused
          onChange={(e) => setQuery(e.target.value)}
          margin="normal"
          placeholder={t('searchForPartNameOrNumber')}
          InputProps={{
            ...params.InputProps,
            className: 'override-class',
            startAdornment: <SearchIcon />,
            endAdornment: options.length ? /* istanbul ignore next */ params.InputProps.endAdornment : undefined,
          }}
        />
      );
    },
    [regex, enabled, partPaths, t]
  );

  const handleOptionChange = useCallback(
    (_event: React.SyntheticEvent, option: SearchablePart) => {
      const {
        part: { id, focal, kits },
        pathHash,
        partNumber,
      } = option;

      if (kits) {
        history.push(makeKitUrl({ productId, externalId: id }));
      } else {
        history.push(
          makePartUrl({
            productId,
            partId: id,
            params: partNumber
              ? /* istanbul ignore next */ {
                  group: pathHash,
                  supersession: partNumber,
                  linkedProductId: focal?.linkedProductId,
                }
              : { group: pathHash, linkedProductId: focal?.linkedProductId },
          })
        );
      }
    },
    [history, productId]
  );

  return (
    <StyledProductSearch<SearchablePart, false, true, false> // <T (Type of options), Multiple, DisableClearable, FreeSolo>
      data-testid="bom-filter-input"
      disableClearable
      autoHighlight
      getOptionLabel={/* istanbul ignore next */ (option) => option.part.name}
      isOptionEqualToValue={
        /* istanbul ignore next */ (option: SearchablePart, value: SearchablePart) =>
          uniqueKey(option) === uniqueKey(value)
      } // Fixes console warings, probably some reference field breaks === comparison
      filterOptions={filterOptionsFunction(regex, enabled)} // Filter function closes over regex
      options={searchOptions}
      noOptionsText={enabled ? t('nothingFound') : t('pleaseEnterMoreCharacters', { min: MINIMUM_SEARCH_TERM_LENGTH })}
      onChange={handleOptionChange}
      inputValue={query}
      // onInputChange={(_, value) => setQuery(value)} // MAG-2972 Remove this if you want to clear the field on change part
      renderInput={renderInput}
      PopperComponent={StyledPopper} // Popper required to target the list item
      renderOption={
        /* istanbul ignore next */ (props, option) => (
          <SearchOption
            key={uniqueKey(option)}
            props={props}
            searchablePart={option}
            regex={regex}
            showKitChip={Boolean(option.part.kits)}
          />
        )
      }
    />
  );
}

export const flattenSupersessionsAsParts = (parts: PartPathWithHash[] = []) =>
  parts
    .map(
      /* istanbul ignore next */ (p) => {
        const partPathWithHash = p as SearchablePart;
        const supersessionEntries = partPathWithHash.part.supersessions?.map((s) => ({ ...p, ...s }));
        return [partPathWithHash, ...(supersessionEntries || [])];
      }
    )
    .flat();

function SearchIcon() {
  return (
    <InputAdornment position="start">
      <WhiteIcon icon={faMagnifyingGlass} />
    </InputAdornment>
  );
}
